import React, { useEffect, useState } from "react";
import { gapi } from "gapi-script";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.tsx';

// Client IDs for OAuth are designed to be public
const API_BASE_URL = "https://api.athenex.xyz";
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, authError, isLoading, loginWithKey } = useAuth();
  const [gapiInitialized, setGapiInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  // If already authenticated, redirect to prompt page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/prompt');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Initialize gapi
    function initializeGapi() {
      if (!clientId) {
        setInitError("Google Client ID is missing. Please check your environment variables.");
        console.error("Google Client ID is missing");
        return;
      }

      try {
        gapi.load("client:auth2", () => {
          gapi.client
            .init({
              clientId: clientId,
              scope: "email",
            })
            .then(() => {
              console.log("Google API initialized");
              setGapiInitialized(true);
              setInitError(null);
            })
            .catch((error) => {
              console.error("Error initializing Google API", error);
              setInitError("Failed to initialize Google authentication.");
            });
        });
      } catch (error) {
        console.error("Error loading Google API", error);
        setInitError("Failed to load Google authentication services.");
      }
    }
    initializeGapi();
  }, []);

  // Verify Google ID token with backend API
  const verifyGoogleToken = async (idToken: string, googleId: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/connection/verify-google`, {
        id_token: idToken
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // If successful, get the API key from the response and login with it
      if (response.data && response.data.api_key) {
        return await loginWithKey(response.data.api_key);
      }
      
      return false;
    } catch (error: any) {
      console.error("API verification error:", error);
      return false;
    }
  };

  const handleLogin = async () => {
    try {
      if (!gapiInitialized) {
        console.error("Google API not initialized yet");
        setInitError("Google authentication is not ready. Please try again in a moment.");
        return;
      }

      const authInstance = gapi.auth2.getAuthInstance();
      if (!authInstance) {
        console.error("Auth instance is null");
        setInitError("Failed to access Google authentication service.");
        return;
      }

      const user = await authInstance.signIn();
      const profile = user.getBasicProfile();

      // Extract Google ID and token
      const googleId = profile.getId();
      const idToken = user.getAuthResponse().id_token;
      
      console.log("User Google ID:", googleId);
      console.log("User Email:", profile.getEmail());
      console.log("User Name:", profile.getName());

      // Verify with backend
      const isAuthorized = await verifyGoogleToken(idToken, googleId);
      
      if (isAuthorized) {
        console.log("Login Success: currentUser:", profile);
        navigate('/prompt');
      }
    } catch (error) {
      console.error("Login failed", error);
      setInitError("Login failed. Please try again.");
    }
  };

  return (
    <div style={styles.container}>
      <img
          loading="lazy"
          src = "./logo.svg"
          alt="Logo"
          className="object-contain aspect-[3.25] w-[400px]"
        />
        {(authError || initError) && (
        <div style={{
          backgroundColor: "#FFEBEE",
          color: "#D32F2F",
          padding: "10px 20px",
          borderRadius: "5px",
          marginBottom: "20px",
          maxWidth: "400px",
          textAlign: "center"
        }}>
          {authError || initError}
        </div>
      )}
      <div style={styles.loginBox}>
        <button 
          onClick={handleLogin} 
          style={styles.loginButton}
          disabled={isLoading || !gapiInitialized}
        >
          {isLoading ? "Signing in..." : !gapiInitialized ? "Loading..." : "Sign in with Google"}
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    gap: "50px",
  },
  logo: {
    marginBottom: "20px",
  },
  loginBox: {
    width: "300px",
    padding: "20px",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    borderRadius: "8px",
    textAlign: "center" as "center",
  },
  loginButton: {
    backgroundColor: "#4285F4",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "5px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
};

export default Login;
